/**
 * Publishing, as a ceremony with steps.
 *
 * > ⚠️ **THIS WAS `PublishPanel`, A CARD AT THE BOTTOM OF THE 17rem RAIL, AND IT HELD FOUR DECISIONS
 * > AT ONCE:** the readiness checklist, the address, the price and the button, stacked in a column
 * > narrower than a phone. The reasoning it was built on is kept because it is still the reasoning —
 * > the checks, the address and what happens next are things somebody has to READ, and a control
 * > that published in one tap would be charging for a decision they had not made. What was wrong was
 * > the shape: four questions in one glance is not a decision, it is a form, and the one act this
 * > product exists to get right was the worst-presented screen in it.
 *
 * ⚠️ IT IS A DIALOG, NOT A PANE, AND THAT IS WHAT MAKES IT A CEREMONY. Publishing is the moment the
 * site stops being private, and a modal is the interface's own way of saying "this one is different
 * from the last two hundred edits". It also fixes the discoverability problem the rail had on a
 * phone, where this was two taps and a scroll behind a menu labelled «Preview».
 *
 * ⚠️ AND IT OPENS ON THE FIRST STEP THAT IS NOT ALREADY DONE. A wizard that makes somebody click
 * «Next» past two ticked steps to republish a typo fix is a wizard they will resent by the third
 * time. Every step is still SHOWN — so the customer can go back to the address — and only the
 * starting point moves.
 *
 * ⚠️ THE PRICE IS NOT ON ANY OF THESE STEPS. Publishing is free for a week (`TRIAL`); what a
 * customer needs to know here is that the site goes up and that it comes down unless they keep it.
 * The number belongs at the checkout, which is where they go when they decide to keep it — quoting
 * it here would be asking for a payment decision in the middle of a different act.
 */

import { useState } from 'react'
import { Button, Field, Input, Notice, Spinner, useLang } from '@ksa/ui'
import { PUBLISH_CONSENT, isHandle, type Issue } from '@ksa/model'
import { ApiFailure, api, publishedUrl } from '../api.ts'
import { useLoad } from '../useLoad.ts'
import { Dialog } from '../Dialog.tsx'

/**
 * ⚠️ FOUR VALUES, AND THE FOURTH IS NOT A STEP. `live` is what the wizard becomes once it has done
 * its job — the dialog the customer is shown the moment their site is on the internet, which is the
 * same furniture and emphatically not a fourth thing to fill in. The indicator stops at three.
 */
type Step = 'checks' | 'address' | 'publish' | 'live'

const STEPS: Exclude<Step, 'live'>[] = ['checks', 'address', 'publish']

export function PublishWizard({
  id,
  handle,
  dirty,
  onGoTo,
  onPublished,
  onAddress,
  onClose,
}: {
  id: string
  handle: string | null
  dirty: boolean
  /** Going to an issue leaves this dialog, so it closes on the way. */
  onGoTo: (where: { pageId?: string; blockId?: string }) => void
  /**
   * ⚠️ THE HEADER HAS TO BE TOLD, BECAUSE IT CANNOT SEE THIS. What a visitor gets is an immutable
   * snapshot (§2.2) and nothing in the document changes when one is written — so without this the
   * header goes on offering «Publish» over a site that is live and identical, until a reload.
   */
  onPublished?: () => void
  /** The editor holds the address too — its header decides whether to offer «Open» from it. */
  onAddress?: (handle: string) => void
  onClose: () => void
}) {
  const { t, lang } = useLang()
  const [checks, recheck] = useLoad((signal) => api.checks(id, signal), [id])
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [live, setLive] = useState<{ url: string; version: number; expiresAt?: string } | null>(null)
  const [claimed, setClaimed] = useState<string | null>(handle)

  /**
   * ⚠️ `null` UNTIL THE CUSTOMER MOVES, AND THEN IT IS THEIRS. The step is derived from the checks
   * while nobody has chosen one — so the dialog opens on the right thing without waiting for a
   * render — and becomes state the moment «Next» or «Back» is pressed, so a `recheck()` cannot
   * teleport somebody out of the step they are reading.
   */
  const [chosen, setChosen] = useState<Step | null>(null)

  /**
   * ⚠️ THE TICK IS LOCAL AND THE **RECORD** IS THE AUTHORITY. `checks.data.consent` is what the
   * server already holds; this is only what the customer has just done. Keying the button on the
   * record alone would ask a first-time publisher for nothing, and keying it on this alone would ask
   * a returning one every single time.
   */
  const [agreed, setAgreed] = useState(false)

  /**
   * ⚠️ THE ADDRESS LIVES HERE RATHER THAN IN THE STEP, BECAUSE «Next» IS WHAT CLAIMS IT. The step
   * used to own the value and a «Take this address» button of its own; the two were never separate
   * decisions, and one field under two buttons is a step somebody can leave without having done the
   * one thing it is for.
   */
  const [address, setAddress] = useState(handle ?? '')
  const [addressFailure, setAddressFailure] = useState<ApiFailure | null>(null)

  const errors = checks.state === 'ready' ? checks.data.errors : []

  /**
   * ⚠️ THE MISSING ADDRESS IS SPLIT OUT OF THE ERRORS, AND WITHOUT THIS THE WIZARD DEADLOCKS. The
   * checks route reports it as an error because it must — the publish refuses without one, and a
   * checklist that came back clean and then failed is the "money first, refusal second" bug the
   * route's own comment is about (§2.9). But it is the ONE error this dialog fixes itself, on the
   * very next step, so blocking «Next» on it means the customer is told to choose an address and
   * given no way to reach the field that chooses it.
   *
   * ⚠️ IT IS MATCHED ON `path`, WHICH IS A CONTRACT WITH THE ROUTE RATHER THAN A GUESS AT A SENTENCE.
   * Matching the message would break the day somebody rewords it, and would break differently in
   * each language.
   */
  const addressMissing = errors.some((issue) => issue.path === 'site.handle')
  const documentErrors = errors.filter((issue) => issue.path !== 'site.handle')

  /**
   * ⚠️ THE CHECK STEP IS SKIPPED ONLY WHEN IT HAS NOTHING TO SAY — no errors AND no warnings.
   * Skipping it whenever it does not BLOCK was the obvious rule and it was wrong: an unfinished site
   * is exactly the one whose warnings somebody should read, and it is also exactly the one that can
   * publish anyway, so the two conditions point in opposite directions. Nothing to report is the
   * only honest reason to walk past a report.
   */
  const nothingToCheck = checks.state === 'ready' && documentErrors.length === 0 && checks.data.warnings.length === 0

  const step: Step =
    live !== null
      ? 'live'
      : (chosen ??
        (checks.state !== 'ready' || !nothingToCheck ? 'checks' : !claimed ? 'address' : 'publish'))

  const say = (failure: ApiFailure) => (lang === 'ar' ? failure.text.ar : failure.text.en)
  const trialDays = checks.state === 'ready' ? checks.data.trialDays : 7

  /** Already on the record, from a previous publish of this site. */
  const consented = checks.state === 'ready' ? checks.data.consent : null
  const mayPublish = Boolean(consented) || agreed

  /**
   * ⚠️ THIS ONE REFUSAL GETS ITS OWN TREATMENT BECAUSE IT IS NOT AN ERROR — IT IS A CONDITION WITH A
   * WAY THROUGH IT. A red banner and a «Publish now» button that will fail identically is the shape
   * of a dead end; the answer the server is asking for is a subscription, so the screen offers one.
   */
  const wantsPayment = failure?.code === 'publish.needs_payment'

  const publish = async () => {
    setBusy(true)
    setFailure(null)
    try {
      const result = await api.publish(id, agreed)
      setLive({ url: result.url, version: result.version, ...(result.published.expiresAt ? { expiresAt: result.published.expiresAt } : {}) })
      onPublished?.()
      recheck()
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Take the address and move on — one act, because it always was one.
   *
   * ⚠️ AN UNCHANGED ADDRESS IS A STEP FORWARD AND NOTHING ELSE. Somebody who comes back to this step
   * to look at the name they already took must not spend a write, and `claimHandle` on the address a
   * site already holds is a no-op the store treats as a success — but a request that cannot fail is
   * still a request that can be slow, on a step whose answer is already on screen.
   *
   * ⚠️ AND A REFUSAL KEEPS THE CUSTOMER HERE. `handle.taken` is the whole reason this is a step: it
   * lands under the field, where the next thing to do is type a different name. Advancing and
   * reporting it on the publish step would be the "money first, refusal second" shape §2.9 is about.
   */
  const takeAddress = async () => {
    if (address === claimed) {
      setChosen('publish')
      return
    }
    setBusy(true)
    setAddressFailure(null)
    try {
      const saved = await api.claimHandle(id, address)
      setClaimed(saved.handle!)
      onAddress?.(saved.handle!)
      recheck()
      setChosen('publish')
    } catch (error) {
      setAddressFailure(error instanceof ApiFailure ? error : null)
    } finally {
      setBusy(false)
    }
  }

  const keepItOnline = async () => {
    setBusy(true)
    setFailure(null)
    try {
      const started = await api.subscribe(id, `/s/${id}`)
      if (started.payUrl) {
        window.location.href = started.payUrl
        return
      }
      recheck()
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
    } finally {
      setBusy(false)
    }
  }

  const goTo = (where: { pageId?: string; blockId?: string }) => {
    onClose()
    onGoTo(where)
  }

  return (
    <Dialog labelledBy="ed-publish-title" onClose={onClose}>
      <h2 className="u-title" id="ed-publish-title">
        {step === 'live' ? t('Your site is live', 'موقعك منشور') : t('Put this site on the internet', 'انشر هذا الموقع')}
      </h2>

      {step !== 'live' && (
        <StepBar step={step} done={{ checks: documentErrors.length === 0, address: Boolean(claimed) && !addressMissing }} />
      )}

      {step === 'checks' && (
        <div className="u-stack u-stack--tight">
          {checks.state === 'loading' && <Spinner label={t('Checking your site', 'جارٍ فحص موقعك')} />}
          {checks.state === 'error' && <Notice tone="error">{say(checks.failure)}</Notice>}

          {checks.state === 'ready' && (
            <>
              {documentErrors.length > 0 && (
                <Notice tone="error" title={t('These must be fixed first', 'يجب إصلاح هذه أولًا')}>
                  <IssueList issues={documentErrors} onGoTo={goTo} />
                </Notice>
              )}

              {/*
                ⚠️ WARNINGS DO NOT BLOCK PUBLISHING, and that is the whole reason they are a separate
                tier. A half-translated site with a picture nobody has taken yet is the normal state
                of a site somebody is still working on; refusing to publish it would mean a generated
                site could never reach a live page at all.
              */}
              {checks.data.warnings.length > 0 && documentErrors.length === 0 && (
                <Notice tone="warn" title={t('Still unfinished — you can publish anyway', 'غير مكتمل — يمكنك النشر مع ذلك')}>
                  <IssueList issues={checks.data.warnings.slice(0, 6)} onGoTo={goTo} />
                  {checks.data.warnings.length > 6 && (
                    <p className="u-tiny" style={{ marginBlockStart: 'var(--u-s2)' }}>
                      {t(`and ${checks.data.warnings.length - 6} more`, `و${checks.data.warnings.length - 6} أخرى`)}
                    </p>
                  )}
                </Notice>
              )}

              {documentErrors.length === 0 && checks.data.warnings.length === 0 && (
                <Notice tone="info">
                  {addressMissing
                    ? /* ⚠️ IT SAYS WHAT IS LEFT RATHER THAN "all clear", because there IS something
                         left and the next step is where it is done. "Everything checks out" over a
                         site that cannot be published is the checklist lying by omission. */
                      t('The pages are fine. Next, the address.', 'الصفحات سليمة. التالي، العنوان.')
                    : t('Everything checks out.', 'كل شيء سليم.')}
                </Notice>
              )}
            </>
          )}
        </div>
      )}

      {step === 'address' && (
        <AddressStep
          value={address}
          claimed={claimed}
          failure={addressFailure}
          onChange={(next) => {
            setAddress(next)
            setAddressFailure(null)
          }}
          onSubmit={() => void takeAddress()}
        />
      )}

      {step === 'publish' && (
        <div className="u-stack u-stack--tight">
          <p className="u-lead">
            {t('It goes live at ', 'سينشر على ')}
            <a className="u-num" href={publishedUrl(claimed ?? '')} target="_blank" rel="noopener noreferrer">
              <span className="u-hostname">{claimed}.ksa-web.app</span>
            </a>
          </p>
          {/*
            ⚠️ THE FREE WEEK IS STATED HERE, BEFORE THE BUTTON, AND WITHOUT A PRICE. What somebody
            needs to decide this is what happens and what happens next — that it goes up now and
            comes down in a week unless they keep it. Quoting the subscription at this moment would
            be asking for a second decision in the middle of the first.
          */}
          <p className="u-small u-faint">
            {t(
              `Free for ${trialDays} days. After that it comes down unless you keep it — you can do that from here the moment it is live.`,
              `مجاني لمدة ${trialDays} أيام. بعدها يُزال ما لم تحتفظ به — ويمكنك ذلك من هنا فور نشره.`,
            )}
          </p>
          {consented ? (
            /* ⚠️ A FACT, NOT A CONTROL. Agreeing happened once; saying so is what stops the customer
               wondering whether they were asked and missed it. */
            <p className="u-tiny u-faint">
              {t('You agreed to the publishing statement on ', 'وافقت على بيان النشر في ')}
              <span className="u-num">{new Date(consented.at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-GB')}</span>
              {t('.', '.')}
            </p>
          ) : (
            <Consent agreed={agreed} onChange={setAgreed} />
          )}
          {dirty && (
            <p className="u-tiny u-faint">{t('Your latest changes are still saving.', 'أحدث تغييراتك قيد الحفظ.')}</p>
          )}
        </div>
      )}

      {step === 'live' && live && (
        <div className="u-stack u-stack--tight">
          <p className="u-lead">
            <a className="u-num" href={live.url} target="_blank" rel="noopener noreferrer">
              <span className="u-hostname">{claimed}.ksa-web.app</span>
            </a>
          </p>
          {/*
            ⚠️ THE HALF MINUTE IS SAID OUT LOUD. The edge caches the pointer that names the live
            version for that long, so a customer who refreshes twice and sees the old page concludes
            publishing did not work — and the next thing they do is publish again.
          */}
          <p className="u-small u-faint">
            {t('Changes can take up to half a minute to appear.', 'قد تستغرق التغييرات حتى نصف دقيقة لتظهر.')}
          </p>
          {live.expiresAt && (
            <Notice tone="warn" title={t(`It stays up for ${trialDays} days`, `يبقى منشورًا ${trialDays} أيام`)}>
              {t(
                'After that it comes down unless you keep it. Nothing is deleted — paying puts it straight back.',
                'بعدها يُزال ما لم تحتفظ به. لا يُحذف شيء — الدفع يعيده فورًا.',
              )}
            </Notice>
          )}
        </div>
      )}

      {failure && <Notice tone={wantsPayment ? 'warn' : 'error'}>{say(failure)}</Notice>}

      {failure?.code === 'publish.invalid' && Array.isArray(failure.details) && (
        <Notice tone="error">
          <IssueList issues={failure.details as Issue[]} onGoTo={goTo} />
        </Notice>
      )}

      <div className="ed__hello__actions">
        {step === 'checks' && (
          <>
            <Button disabled={checks.state !== 'ready' || documentErrors.length > 0} onClick={() => setChosen('address')}>
              {t('Next', 'التالي')}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t('Not now', 'ليس الآن')}
            </Button>
          </>
        )}

        {step === 'address' && (
          <>
            {/* ⚠️ IT CLAIMS AND THEN MOVES ON. See `takeAddress` and `AddressStep`'s note: the step
                used to carry a second button that did the first half. */}
            <Button busy={busy} disabled={!isHandle(address)} onClick={() => void takeAddress()}>
              {t('Next', 'التالي')}
            </Button>
            <Button variant="quiet" onClick={() => setChosen('checks')}>
              {t('Back', 'رجوع')}
            </Button>
          </>
        )}

        {step === 'publish' && (
          <>
            {/*
              ⚠️ WHEN THE SERVER HAS ASKED FOR A SUBSCRIPTION, THAT BECOMES THE PRIMARY ACTION AND
              «Publish now» STAYS AVAILABLE. Removing the publish button would strand somebody who
              pays in another tab; making the checkout secondary would leave the only route forward
              looking like the thing they had already tried.
            */}
            {wantsPayment && (
              <Button busy={busy} onClick={keepItOnline}>
                {t('Keep it online', 'أبقِه منشورًا')}
              </Button>
            )}
            <Button
              variant={wantsPayment ? 'quiet' : 'primary'}
              busy={busy}
              disabled={errors.length > 0 || !mayPublish}
              onClick={publish}
            >
              {t('Publish now', 'انشر الآن')}
            </Button>
            <Button variant="quiet" onClick={() => setChosen('address')}>
              {t('Back', 'رجوع')}
            </Button>
          </>
        )}

        {step === 'live' && (
          <>
            {/*
              ⚠️ «DONE» IS THE PRIMARY ONE AND KEEPING IT IS SECOND, WHICH IS §5.7 APPLIED TO THE
              MOMENT IT IS HARDEST TO APPLY. The customer has just published; this is exactly when an
              upsell converts best and exactly when a product that asks reads as having been waiting
              for it. The offer is on the screen, it is not the thing being pressed, and the site
              card goes on offering it every day of the week.
            */}
            <Button onClick={onClose}>{t('Done', 'تم')}</Button>
            {live?.expiresAt && (
              <Button variant="quiet" busy={busy} onClick={keepItOnline}>
                {t('Keep it online', 'أبقِه منشورًا')}
              </Button>
            )}
          </>
        )}
      </div>
    </Dialog>
  )
}

/**
 * Where you are, and how far there is to go.
 *
 * ⚠️ IT IS TEXT AND A TICK, NOT A PROGRESS BAR. Three steps is a number somebody can hold; a bar
 * would be a picture of a number that is already written down. The done steps are marked because a
 * wizard that opens on step three has to explain why it did.
 */
function StepBar({ step, done }: { step: Step; done: { checks: boolean; address: boolean } }) {
  const { t } = useLang()
  const label: Record<Exclude<Step, 'live'>, string> = {
    checks: t('Check', 'الفحص'),
    address: t('Address', 'العنوان'),
    publish: t('Publish', 'النشر'),
  }

  return (
    <ol className="ed__steps">
      {STEPS.map((name, i) => {
        const isDone = name === 'checks' ? done.checks : name === 'address' ? done.address : false
        const current = name === step
        return (
          <li key={name} className={`ed__step ${current ? 'ed__step--current' : ''} ${isDone ? 'ed__step--done' : ''}`}>
            <span className="ed__step__n" aria-hidden="true">
              {isDone && !current ? '✓' : i + 1}
            </span>
            {label[name]}
          </li>
        )
      })}
    </ol>
  )
}

function IssueList({ issues, onGoTo }: { issues: Issue[]; onGoTo: (where: { pageId?: string; blockId?: string }) => void }) {
  const { lang } = useLang()
  return (
    <ul className="u-stack u-stack--tight" style={{ marginBlockStart: 'var(--u-s2)' }}>
      {issues.map((issue, i) => (
        <li key={i}>
          {issue.where ? (
            // ⚠️ EVERY ISSUE CARRIES WHERE IT IS, so this is a link rather than a description. A
            // publish screen that lists problems without a way to reach them sends the customer
            // hunting through their own pages — which is the point at which they stop.
            <button
              className="u-btn u-btn--ghost u-btn--sm"
              style={{ textAlign: 'start', whiteSpace: 'normal' }}
              onClick={() => onGoTo(issue.where!)}
            >
              {lang === 'ar' ? issue.message.ar : issue.message.en} →
            </button>
          ) : (
            <span className="u-small">{lang === 'ar' ? issue.message.ar : issue.message.en}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

/**
 * Choosing the address, which is the first moment this site takes a name.
 *
 * ⚠️ IT IS ITS OWN STEP BECAUSE IT MUST HAPPEN BEFORE THE PUBLISH AND CAN FAIL. Building reserves
 * nothing (§2.4, `SiteRecord.handle`), so two people can draft towards the same address and exactly
 * one gets it. `handle.taken` is shown here, at the moment of claiming, where the customer can pick
 * another — rather than after the publish, which is the version of this that would be indefensible.
 *
 * > ⚠️ **THIS STEP HAD A «Take this address» BUTTON OF ITS OWN, AND «Next» BESIDE IT.** Two controls
 * > for one act: typing a name and then pressing two buttons in the right order, on a step whose
 * > whole content is one field. Reported as *"this step doesn't need «take this address», the next
 * > button should do that"* — and it is right, because the two were never separable decisions. There
 * > is nothing a customer would want to do on this step other than take the address they just typed
 * > and move on, so the button that moves on is the button that takes it.
 * >
 * > What is kept from the old arrangement is the REASON it existed: claiming can fail, and the
 * > failure has to land beside the field rather than after the publish. It still does — «Next» stays
 * > on this step and shows `handle.taken` under the input, which is why the claim is awaited here
 * > rather than folded into the publish request.
 *
 * ⚠️ IT HOLDS NO STATE OF ITS OWN ANY MORE, AND THAT IS WHAT MADE THE CHANGE POSSIBLE. The value and
 * the claim moved up to the wizard, because the control that commits them is in the wizard's own
 * footer — a step that owned the value and a footer that owned the button could only have been
 * joined by a ref, which is a lot of machinery for one string.
 */
function AddressStep({
  value,
  claimed,
  failure,
  onChange,
  onSubmit,
}: {
  value: string
  claimed: string | null
  failure: ApiFailure | null
  onChange: (next: string) => void
  /** Enter is «Next». A single-field step where the keyboard cannot finish it is a step that argues
   *  with every phone keyboard's own «Go». */
  onSubmit: () => void
}) {
  const { t, lang } = useLang()

  return (
    <div className="u-stack u-stack--tight">
      <Field
        label={claimed ? t('Its address', 'عنوانه') : t('Choose an address', 'اختر عنوانًا')}
        hint={
          claimed
            ? t('Change it any time before you publish.', 'يمكنك تغييره في أي وقت قبل النشر.')
            : t('English letters, digits and hyphens.', 'أحرف إنجليزية وأرقام وشرطات.')
        }
        {...(failure ? { error: lang === 'ar' ? failure.text.ar : failure.text.en } : {})}
      >
        {(a) => (
          /* One identifier — the box and its suffix are isolated together, or the domain reads
             backwards in Arabic. See `.u-idgroup`. */
          <div className="u-row u-idgroup" style={{ gap: 'var(--u-s2)', flexWrap: 'nowrap' }}>
            <Input
              {...a}
              dir="ltr"
              value={value}
              placeholder="your-name"
              onChange={(e) => onChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isHandle(value)) {
                  e.preventDefault()
                  onSubmit()
                }
              }}
            />
            <span className="u-num u-small u-faint" style={{ whiteSpace: 'nowrap' }}>
              .ksa-web.app
            </span>
          </div>
        )}
      </Field>
    </div>
  )
}

/**
 * What the customer says before their site goes out.
 *
 * ⚠️ **THE STATEMENTS ARE VISIBLE TEXT AND THE CHECKBOX'S OWN LABEL IS SHORT.** Putting three
 * clauses inside the `<label>` makes a target the width of a paragraph — every attempt to read it on
 * a phone toggles it — and it makes the announced label a speech. The list is `aria-describedby` on
 * the input instead, so a screen reader gets "I confirm all of the above" and then the clauses.
 *
 * ⚠️ AND IT IS A NATIVE CHECKBOX. Same argument §3.3 makes for the destination radios: the platform
 * control brings its own focus ring, its own space-bar handling and its own announcement, all of
 * which a styled div reimplements and usually reimplements wrong — on the one control in this
 * product that is a statement somebody is making rather than a preference they are setting.
 *
 * ⚠️ THE THIRD CLAUSE DISCLOSES THE SUBSCRIPTION CONDITION, AND THAT PLACEMENT IS THE POINT. Some
 * sites are asked to pay instead of taking the free week (CLAUDE.md §5.12); the only fair moment to
 * say so is before the button, because a customer who first learns it from a refusal learns it as an
 * accusation.
 */
function Consent({ agreed, onChange }: { agreed: boolean; onChange: (next: boolean) => void }) {
  const { t, lang } = useLang()
  const clauses = lang === 'ar' ? PUBLISH_CONSENT.ar : PUBLISH_CONSENT.en

  return (
    <div className="ed__consent">
      <ul className="u-stack u-stack--tight u-small" id="ed-consent-terms">
        {clauses.map((clause) => (
          <li key={clause}>{clause}</li>
        ))}
      </ul>
      <label className="ed__consent__tick">
        <input
          type="checkbox"
          checked={agreed}
          aria-describedby="ed-consent-terms"
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="u-small">{t('I confirm all of the above.', 'أؤكّد كل ما سبق.')}</span>
      </label>
    </div>
  )
}
